import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ENV } from '@environment';
import _ from 'lodash';
import { RootState } from 'store';

import FiltersBottomSheet, { FilterItem, PriceFilter } from 'components/FiltersBottomSheet';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import useIsDesktop from 'hooks/useIsDesktop';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Airport } from 'models/airport';
import { FlightFilterResponse, FlightFilter, DepartureTime, FlightAirlineFilter } from 'models/flightFilter';
import { amplitudelogEvent, FilterGroupEvent, SelectFilterEvent } from 'services/amplitudeService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount } from 'utils/generic';

import Airlines from './components/Airlines';
import DepartureTimes from './components/DepartureTimes';
import LoaderDesktop from './components/LoaderDesktop';
import Stops from './components/Stops';

interface Props {
  show: boolean;
  currency: string;
  directFlights: boolean;
  defaultFilters?: FlightFilter;
  availableFilters?: FlightFilterResponse;
  onSearch: (filters: FlightFilter) => void;
  onClose: () => void;
  roundTrip?: boolean;
  baggageIncluded?: boolean;
  cabin?: string;
  departurePoint?: Airport;
  arrivalPoint?: Airport;
  departureDate?: Date;
  returnDate?: Date;
  isLoading: boolean;
}

interface AvailableDepartureTimes {
  text: string;
  from: number;
  to: number;
}

const TIMEOUT_INTERVAL = 700;

export const FlightsFilters = ({
  directFlights,
  defaultFilters,
  availableFilters,
  currency,
  onClose,
  onSearch,
  roundTrip,
  baggageIncluded,
  cabin,
  departurePoint,
  arrivalPoint,
  departureDate,
  returnDate,
  isLoading,
  ...props
}: Props) => {
  const { t } = useStoreTranslation();

  const isDesktop = useIsDesktop();
  const [changed, setChanged] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<number>();
  const [stops, setStops] = useState<string[]>(defaultFilters?.stops || []);
  const [airlines, setAirlines] = useState<string[]>(defaultFilters?.airlines || []);
  const [departureTimes, setDepartureTimes] = useState<DepartureTime[]>(defaultFilters?.departureTimes || []);
  const [price, setPrice] = useState<number[]>(
    defaultFilters?.priceFrom && defaultFilters.priceTo ? [defaultFilters?.priceFrom, defaultFilters.priceTo] : [],
  );
  const [minMax, setMinMax] = useState<number[]>(
    defaultFilters?.priceFrom && defaultFilters.priceTo ? [defaultFilters?.priceFrom, defaultFilters.priceTo] : [],
  );
  const [availableDepartureTimes, setAvailableDepartureTimes] = useState<AvailableDepartureTimes[]>([]);

  const [filtersCount, setFiltersCount] = useState(0);

  const search = useSelector((state: RootState) => state.flights.search);
  const user = useSelector((state: RootState) => state.user.accountResult);

  const sendAmplitudeEvent = useCallback(
    (filterGroup: FilterGroupEvent, filterValue: any) => {
      const amplitudeSelectFilter: SelectFilterEvent = {
        FILTER_GROUP: filterGroup,
        FILTER_VALUE: filterValue.toString(),
        TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
        TRAVEL_FROM: departurePoint?.code ?? '',
        TRAVEL_TO: arrivalPoint?.code ?? '',
        TRAVEL_DESTINATION_TYPE: '',
        TRAVEL_TRIP_TYPE: roundTrip ? 'true' : 'false',
        TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
        TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
        TRAVEL_DATE_TO: returnDate?.toString() ?? '',
        TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
        TRAVEL_PASSENGERS: '1',
        TRAVEL_ADULTS: '1',
        TRAVEL_CHILDREN: '0',
        TRAVEL_INFANTS: '0',
        TRAVEL_CLASS_OF_SERVICE: cabin ?? '',
        TRAVEL_ROOMS: '',
        TRAVEL_WITH_BAGGAGE: baggageIncluded ? 'true' : 'false',
        TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
        SEARCHID: search?.searchId ?? '',
      };

      amplitudelogEvent(amplitudeEvents.SELECT_FILTER, amplitudeSelectFilter, user);
    },
    [
      arrivalPoint?.code,
      baggageIncluded,
      cabin,
      departureDate,
      departurePoint?.code,
      returnDate,
      roundTrip,
      search?.searchId,
      user,
    ],
  );

  const handleSearchPrice = useCallback(
    (values: number[]) => {
      setPrice(values);
      let timeoutId = searchTimeout;
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        if (isDesktop) setChanged(true);
        if (values.length > 0) sendAmplitudeEvent(FilterGroupEvent.PRICE, values);
      }, TIMEOUT_INTERVAL);
      setSearchTimeout(timeoutId);
    },
    [isDesktop, searchTimeout, sendAmplitudeEvent],
  );

  const handleDepartureTimes = useCallback(
    (values: DepartureTime[]) => {
      setDepartureTimes(values);

      let timeoutId = searchTimeout;
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        if (isDesktop) setChanged(true);

        if (values.length > 0) {
          //Amplitude
          const amplitudeValues: string[] = [];
          for (const d of values) {
            const text = `sector: ${d.sectorNumber} - from: ${d.from} - to: ${d.to}`;
            amplitudeValues.push(text);
          }
          sendAmplitudeEvent(FilterGroupEvent.DEPARTURE_TIME, amplitudeValues);
        }
      }, TIMEOUT_INTERVAL);
      setSearchTimeout(timeoutId);
    },
    [isDesktop, searchTimeout, sendAmplitudeEvent],
  );

  useEffect(() => {
    if (!directFlights) {
      setStops(stops.filter((stop) => stop !== '0'));
    } else {
      setStops([...stops, '0']);
    }
  }, [directFlights]); // eslint-disable-line react-hooks/exhaustive-deps
  // TODO: check dependencies or refactor

  useEffect(() => {
    if (
      availableFilters &&
      availableFilters.minPrice &&
      availableFilters.maxPrice &&
      !defaultFilters?.priceFrom &&
      !defaultFilters?.priceTo
    ) {
      setPrice([availableFilters.minPrice, availableFilters.maxPrice]);
    }
  }, [availableFilters, defaultFilters]);

  useEffect(() => {
    if (availableFilters && availableFilters.minPrice && availableFilters.maxPrice) {
      setMinMax([availableFilters.minPrice, availableFilters.maxPrice]);
    }
    if (availableFilters && availableFilters.departureTimes) {
      setAvailableDepartureTimes(availableFilters.departureTimes);
    }
  }, [availableFilters]);

  useEffect(() => {
    let count = 0;

    //Count para Aerolineas
    if (airlines && airlines.length > 0) {
      count += airlines.length;
    }

    //Count para Paradas
    if (stops && stops.length > 0) {
      count += _.uniq(stops).length;
    }

    //Count para Precios
    if (price && minMax && (price.first() !== minMax.first() || price.last() !== minMax.last())) {
      count++;
    }

    //Count para Salidas
    if (departureTimes && availableDepartureTimes && departureTimes.length > 0 && availableDepartureTimes.length > 0) {
      const result = departureTimes.filter((departureTime) => {
        const sector = departureTime.sectorNumber - 1;
        const { from, to } = availableDepartureTimes[sector];
        return from !== Number(departureTime.from.substr(0, 2)) || to !== Number(departureTime.to.substr(0, 2));
      });

      count += result.length;
    }

    setFiltersCount(count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airlines, stops, departureTimes, price, minMax, availableDepartureTimes]);

  const handleSearch = useCallback(() => {
    const filter: FlightFilter = {};
    if (airlines.length > 0) filter.airlines = airlines;
    if (stops.length > 0) filter.stops = stops;
    if (departureTimes.length > 0) filter.departureTimes = departureTimes;
    if (price.first() !== minMax.first() || price.last() !== minMax.last()) {
      filter.priceFrom = price.first();
      filter.priceTo = price.last();
    }

    onSearch(filter);
  }, [airlines, departureTimes, minMax, onSearch, price, stops]);

  const handleClearFilters = useCallback(() => {
    setAirlines([]);
    setStops([]);
    setDepartureTimes([]);
    setPrice(minMax);
  }, [minMax]);

  const countAirlines: FlightAirlineFilter[] = useMemo(() => {
    if (!availableFilters) return [] as FlightAirlineFilter[];
    const { airlines } = availableFilters;
    const aux: { [key: string]: FlightAirlineFilter[] } = {};
    airlines.forEach((a) => {
      aux[a.text] = [...(aux[a.text] ?? []), a];
    });
    const res = Object.entries(aux).map(([k, v]) => ({
      count: v.length > 1 ? v.reduce((a, b) => a + b.count, 0) : v[0].count,
      text: v[0].text,
      airlineName: v[0].airlineName,
    }));
    return res;
  }, [availableFilters]);

  useEffect(() => {
    if (typeof defaultFilters === 'undefined') {
      handleClearFilters();
      return;
    }
    setAirlines(defaultFilters?.airlines ? defaultFilters.airlines : []);
    setStops(defaultFilters?.stops ? defaultFilters.stops : []);
    setDepartureTimes(defaultFilters?.departureTimes ? defaultFilters.departureTimes : []);
    setPrice(
      defaultFilters?.priceFrom && defaultFilters?.priceTo
        ? [defaultFilters.priceFrom, defaultFilters.priceTo]
        : minMax,
    );
  }, [defaultFilters, handleClearFilters, minMax]);

  useEffect(() => {
    if (isDesktop && changed) {
      handleSearch();
      setChanged(false);
    }
  }, [changed, handleSearch, isDesktop]);

  const renderContent = useMemo(() => {
    const filters = availableFilters;
    if (!filters) return <></>;
    return (
      <>
        <FilterItem
          name={t('flightsFilters.stops.title')}
          count={_.uniq(stops).length}
          isOpen={isDesktop}
          className={isDesktop ? 'no-border' : ''}
        >
          <Stops
            values={stops}
            onChange={(value) => {
              setStops(value);
              if (isDesktop) setChanged(true);
            }}
            onOptionChange={(value, isChecked) => {
              if (isChecked) sendAmplitudeEvent(FilterGroupEvent.STOPS, value);
            }}
            stops={filters.stops}
          />
        </FilterItem>

        <DepartureTimes
          departureTimes={filters.departureTimes.map((departureTime, index) => ({
            ...departureTime,
            sectorNumber: Number(departureTime.text.replace('Sector', '')),
            text: filters.flightDurations[index].text.replace('|', ' - '),
          }))}
          values={departureTimes}
          onChange={handleDepartureTimes}
          isOpen={isDesktop}
          className={isDesktop ? 'no-border' : ''}
        />

        <FilterItem
          name={t('flightsFilters.airlines.title')}
          count={airlines.length}
          isOpen={isDesktop}
          className={isDesktop ? 'no-border' : ''}
        >
          <Airlines
            airlines={countAirlines}
            values={airlines}
            onChange={(values) => {
              setAirlines(values);
              if (isDesktop) setChanged(true);
            }}
            onOptionChange={(value, isChecked) => {
              if (isChecked) sendAmplitudeEvent(FilterGroupEvent.AEROLINEA, value);
            }}
          />
        </FilterItem>

        <FilterItem
          name={t('flightsFilters.price.title')}
          changed={price.first() !== filters.minPrice || price.last() !== filters.maxPrice}
          isOpen={isDesktop}
          className={isDesktop ? 'no-border' : ''}
        >
          <PriceFilter
            min={filters.minPrice}
            max={filters.maxPrice}
            value={price}
            currency={currency}
            onChange={handleSearchPrice}
          />
        </FilterItem>
      </>
    );
  }, [
    airlines,
    availableFilters,
    countAirlines,
    currency,
    departureTimes,
    handleDepartureTimes,
    handleSearchPrice,
    isDesktop,
    price,
    sendAmplitudeEvent,
    stops,
    t,
  ]);

  return (
    <>
      <Mobile>
        <FiltersBottomSheet
          {...props}
          showSearchButton={Boolean(availableFilters)}
          onClose={onClose}
          onSearch={handleSearch}
          filtersCount={filtersCount}
          onClearFilter={handleClearFilters}
        >
          {!availableFilters ? <Loader fullScreen /> : renderContent}
        </FiltersBottomSheet>
      </Mobile>
      <Desktop>{isLoading ? <LoaderDesktop /> : availableFilters && renderContent}</Desktop>
    </>
  );
};

export default memo(FlightsFilters);
